import { memo, useCallback } from 'react';
import { Badge, Button, Card } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { PERMISSIONS } from '../../constants/permissions';
import Can from '../guards/Can';

const DAY = 86_400_000;

/** "14 days ago" (the prototype counts days, not weeks); today when it is less than a day. */
const daysSince = (iso, now = Date.now()) =>
  iso ? Math.max(0, Math.floor((now - new Date(iso).getTime()) / DAY)) : null;

/**
 * One role of the member: name, System badge, description, permission count, who assigned it and
 * when, and the Edit role / Remove actions. Edit role opens the role page, Remove asks first.
 *
 * @param {object} props
 * @param {{ id: string, name: string, description: string, isSystem: boolean,
 *   permissionCount: number, assignedBy: string, assignedAt: string|null }} props.role
 * @param {(roleId: string) => void} props.onEdit
 * @param {(role: object) => void} props.onRemove
 */
function MemberRoleCard({ role, onEdit, onRemove }) {
  const { t } = useTranslation('people-access');
  const days = daysSince(role.assignedAt);
  const when =
    days === null
      ? ''
      : days < 1
        ? t('memberDetail.values.today')
        : t('memberDetail.relative.daysAgo', { count: days });

  const handleEdit = useCallback(() => onEdit(role.id), [onEdit, role.id]);
  const handleRemove = useCallback(() => onRemove(role), [onRemove, role]);

  return (
    <Card className="flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3" data-testid={`member-role-${role.id}`}>
      <div className="flex min-w-0 flex-1 basis-64 flex-col gap-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-sm font-semibold text-content">{role.name}</h3>
          {role.isSystem ? <Badge variant="neutral">{t('memberTabs.roles.system')}</Badge> : null}
        </div>
        <p className="text-xs text-content-muted">{role.description}</p>
      </div>
      <div className="flex flex-col items-start gap-0.5 text-xs text-content-muted sm:items-end">
        <span className="text-content-secondary">
          {t('memberTabs.roles.permissions', { count: role.permissionCount })}
        </span>
        <span>{t('memberTabs.roles.assignedBy', { by: role.assignedBy, when })}</span>
      </div>
      <div className="flex items-center gap-1">
        <Can permission={PERMISSIONS.ROLES_VIEW}>
          <Button variant="ghost" size="sm" aria-label={t('memberTabs.roles.editRoleAria', { name: role.name })} onClick={handleEdit}>
            {t('memberTabs.roles.editRole')}
          </Button>
        </Can>
        <Can permission={PERMISSIONS.MEMBERS_MANAGE_ACCESS}>
          <Button variant="ghost" size="sm" aria-label={t('memberTabs.roles.removeAria', { name: role.name })} onClick={handleRemove}>
            {t('memberTabs.roles.remove')}
          </Button>
        </Can>
      </div>
    </Card>
  );
}

export default memo(MemberRoleCard);
